import clsx from 'clsx';
import {
  type ClipboardEventHandler,
  type FocusEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import Borders from '@dynatrace/strato-design-tokens/borders';
import { CopyIcon } from '@dynatrace/strato-icons';

import { CodeHighlight } from './CodeHighlight.js';
import * as styles from './CodeSnippet.sty.js';
import type { HighlightLanguage } from './HighlightExtended.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { devLog } from '../../core/utils/logging.js';
import { useContainerFocus } from '../../forms/hooks/useContainerFocus.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

const COMPONENT_NAME = 'CodeSnippet';

/**
 * Props related to the height of the `CodeSnippet` component.
 * @public
 */
export type CodeSnippetHeightProps =
  | {
      /**
       * Height of the code container. If this is known in advance, then very long
       * code snippets will still be able to fit into the container while also
       * having scrolling enabled. Value is given in px.
       */
      maxHeight?: number;
      fullHeight?: false;
    }
  | {
      /**
       * Whether the code snippet should take up the available height of its parent container.
       * @defaultValue false
       */
      fullHeight: true;
      maxHeight?: never;
    };

/**
 * Props for the `CodeSnippet` component.
 * @public
 */
export type CodeSnippetProps = WithChildren &
  StylingProps &
  DataTestId &
  MaskingProps &
  AriaLabelingProps &
  BehaviorTrackingProps &
  CodeSnippetHeightProps & {
    /**
     * Code language to be used to run highlighting on the given code.
     */
    language?: HighlightLanguage;
    /**
     * Whether the copy button should be displayed.
     * @defaultValue true
     * @deprecated Will be removed. Use 'showCopyAction' instead
     */
    allowCopy?: boolean;
    /**
     * Whether the copy button should be displayed.
     * @defaultValue true
     */
    showCopyAction?: boolean;
    /**
     * Whether line numbers should be shown.
     * @defaultValue true
     */
    showLineNumbers?: boolean;
    /**
     * Whether the content breaks into new lines automatically or not.
     * @defaultValue false
     */
    lineBreaks?: boolean;
    /**
     * Callback fired when copy button is clicked and the code snippet was copied.
     */
    onCopy?: () => void;
    /**
     * CodeSnippet layout size, 'default' for standard spacing and 'condensed' for reduced font size, padding and margins.
     * @defaultValue 'default'
     */
    size?: 'default' | 'condensed';
    /**
     * Called when the component receives focus.
     */
    onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
    /**
     * Called when the component loses focus.
     */
    onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
    /**
     * Handler that is called when the element's focus status changes.
     * @deprecated Use onFocus/onBlur instead.
     */
    onFocusChange?: (isFocused: boolean) => void;
  };

/**
 * Use the `CodeSnippet` to display a code block in a read-only context.
 * By default, the code is formatted but not highlighted.
 * @public
 */
export const CodeSnippet = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  CodeSnippetProps
>((props, forwardedRef) => {
  const {
    children,
    language,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    allowCopy: allowCopyProp,
    showCopyAction: showCopyActionProp,
    showLineNumbers = true,
    lineBreaks = false,
    maxHeight,
    onCopy,
    size = 'default',
    fullHeight = false,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    style: consumerStyle,
    className: consumerClassName,
    onFocus,
    onBlur,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const showCopyAction = showCopyActionProp ?? allowCopyProp ?? true;

  const { containerClassName, ...containerFocusProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
    });

  const joinedChildren = useMemo(
    () => (Array.isArray(children) ? children.join('') : children),
    [children],
  );

  const copyText = `${joinedChildren?.toString() ?? ''}`;

  const [hasCopied, setHasCopied] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const intl = useIntl();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {
      devLog(
        'CodeSnippet: clipboard access was blocked (restricted iframe or strict CSP). Nothing was copied.',
      );
      return;
    }
    setHasCopied(true);
    setIsTooltipOpen(true);
    onCopy?.();
  };

  const copySelection: ClipboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const selection = document.getSelection();
      const clipboardData = event.nativeEvent.clipboardData;
      if (selection === null) {
        return;
      }
      if (clipboardData === null) {
        devLog(
          'CodeSnippet: clipboard access was blocked (restricted iframe or strict CSP). Nothing was copied.',
        );
        return;
      }
      // Firefox tends to add a new line separator per div (with user-select: none; css style rule)
      // in the selection if the selection spans multiple divs (one div per line).
      // See https://bugzilla.mozilla.org/show_bug.cgi?id=1273836
      clipboardData.setData('text/plain', selection.toString());
      event.preventDefault();
    },
    [],
  );

  const copyLabel = intl.formatMessage({
    defaultMessage: 'Copy',
    id: 'Yi8j4dchtpkwn/OB',
    description: 'Shown for elements that trigger a copy to clipboard',
  });

  const copiedLabel = intl.formatMessage({
    defaultMessage: 'Copied',
    id: 'THNwSvfvDSXAUapi',
    description: 'Shown after copying to clipboard',
  });

  const message = useMemo(() => {
    return hasCopied ? copiedLabel : copyLabel;
  }, [hasCopied, copiedLabel, copyLabel]);

  const codeHighlightRef = useRef<HTMLPreElement | null>(null);

  return (
    <div
      ref={forwardedRef}
      onCopy={copySelection}
      className={clsx(consumerClassName, containerClassName, styles.wrapper, {
        [styles.wrapperFullHeight]: fullHeight,
        [styles.wrapperGutter({ size })]: showLineNumbers,
      })}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...containerFocusProps}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      <CodeHighlight
        ref={codeHighlightRef}
        language={language}
        code={copyText}
        showLineNumbers={showLineNumbers}
        lineBreaks={lineBreaks}
        maxHeight={maxHeight}
        size={size}
      />
      {showCopyAction ? (
        <div className={styles.copyButtonContainer({ size })}>
          <Tooltip
            text={message}
            placement="top"
            open={isTooltipOpen}
            delay="none"
            onOpenChange={(isOpen) => {
              setIsTooltipOpen(isOpen);
              if (!isOpen) {
                setHasCopied(false);
              }
            }}
          >
            <Button
              variant="default"
              size={size}
              aria-label={copyLabel}
              onClick={() => void handleCopy()}
              style={{ borderRadius: Borders.Radius.Field.Subdued }}
              data-dt-children-name="Copy"
            >
              <Button.Prefix>
                <CopyIcon />
              </Button.Prefix>
            </Button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
});

(CodeSnippet as typeof CodeSnippet & { displayName: string }).displayName =
  COMPONENT_NAME;
